import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, uitvoeren } from './client.ts';
import { useApiMutatie } from './mutaties.ts';
import type { GebruikerInvoer, Gebruikersstatus, GebruikerWijziging, Paginering } from './typen.ts';

export const gebruikerSleutels = {
  alle: ['gebruikers'] as const,
  lijst: (status: Gebruikersstatus, paginering: Paginering) => ['gebruikers', 'lijst', status, paginering] as const,
  details: (id: string) => ['gebruikers', id] as const,
};

/**
 * Somt de gebruikers met een status op.
 * @param status De status.
 * @param paginering Limiet en offset.
 * @returns De query met de gebruikers.
 */
export function useGebruikers(status: Gebruikersstatus, paginering: Paginering = { limiet: 500 }) {
  return useQuery({
    queryKey: gebruikerSleutels.lijst(status, paginering),
    queryFn: () => uitvoeren(api.GET('/api/v1/gebruikers', { params: { query: { status, ...paginering } } })),
    placeholderData: keepPreviousData,
  });
}

/**
 * Haalt een gebruiker op, bijvoorbeeld om de naam te tonen bij een gesprek.
 * @param id Het id van de gebruiker.
 * @returns De query met de gebruiker.
 */
export function useGebruiker(id: string | null | undefined) {
  return useQuery({
    queryKey: gebruikerSleutels.details(id ?? ''),
    queryFn: () => uitvoeren(api.GET('/api/v1/gebruikers/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: Boolean(id),
    staleTime: 5 * 60_000,
  });
}

/**
 * De mutaties op gebruikers, voor beheerders.
 * @returns De mutaties.
 */
export function useGebruikerMutaties() {
  const verversen = () => [gebruikerSleutels.alle];
  return {
    maak: useApiMutatie(
      (invoer: GebruikerInvoer) => uitvoeren(api.POST('/api/v1/gebruikers', { body: invoer })),
      verversen,
    ),
    wijzig: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: GebruikerWijziging }) =>
        uitvoeren(api.PATCH('/api/v1/gebruikers/{id}', { params: { path: { id } }, body: invoer })),
      verversen,
    ),
    activeer: useApiMutatie(
      (id: string) => uitvoeren(api.POST('/api/v1/gebruikers/{id}/activering', { params: { path: { id } } })),
      verversen,
    ),
    deactiveer: useApiMutatie(
      (id: string) => uitvoeren(api.POST('/api/v1/gebruikers/{id}/deactivering', { params: { path: { id } } })),
      verversen,
    ),
    nodigUit: useApiMutatie(
      (id: string) => uitvoeren(api.POST('/api/v1/gebruikers/{id}/uitnodigingen', { params: { path: { id } } })),
      verversen,
    ),
  };
}
